import React, { useEffect, useState } from 'react';
import {
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Box,
  Typography,
  useMediaQuery
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFacebook,
  faTwitter,
  faWhatsapp,
  faSnapchatGhost,
  faInstagram,
  faFacebookF,
  faXTwitter
} from '@fortawesome/free-brands-svg-icons';
import { faLink, faShare } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import domtoimage from 'dom-to-image';
import { BASE_URL } from '../../api/constants';
import { useClientApi } from '../../api/client';
import { CLIENT_TYPES } from '../../api/clientconfig';
import { identifyClientType } from '../../utils/identifyClientType';


// Share utility functions
// Helper function to detect mobile device
const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

// Helper function to open in new tab
const openInNewTab = (url) => {
  const width = 600;
  const height = 400;
  const left = (window.innerWidth - width) / 2;
  const top = (window.innerHeight - height) / 2;

  return window.open(
    url,
    '_blank',
    `width=${width},height=${height},top=${top},left=${left}`
  );
};

// Updated ShareOnWhatsApp function
const shareOnWhatsApp = async (imageUrl, text = 'Check out my health results!') => {
  try {
    const whatsAppShareUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(`${text} ${imageUrl}`)}`;
    openInNewTab(whatsAppShareUrl);
  } catch (error) {
    console.error('Failed to share on WhatsApp:', error);
    throw error;
  }
};

// Updated ShareOnFacebook function
const shareOnFacebook = async (imageUrl) => {
  try {
    const encodedUrl = encodeURIComponent(imageUrl);
    const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
    openInNewTab(fbUrl);
  } catch (error) {
    console.error('Failed to share on Facebook:', error);
    throw error;
  }
};

// Updated ShareOnTwitter function
const shareOnTwitter = async (imageUrl, text = 'Check out my health results!') => {
  try {
    const encodedText = encodeURIComponent(text);
    const encodedUrl = encodeURIComponent(imageUrl);
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`;
    openInNewTab(twitterUrl);
  } catch (error) {
    console.error('Failed to share on Twitter:', error);
    throw error;
  }
};

// Updated ShareOnSnapchat function
const shareOnSnapchat = async (imageUrl) => {
  try {
    const encodedUrl = encodeURIComponent(imageUrl);
    const snapchatUrl = `https://www.snapchat.com/share?url=${encodedUrl}`;

    if (isMobileDevice()) {
      // For mobile devices, try to open in Snapchat app
      window.location.href = snapchatUrl;
    } else {
      // For desktop, open in new tab
      openInNewTab(snapchatUrl);
    }
  } catch (error) {
    console.error('Failed to share on Snapchat:', error);
    throw error;
  }
};

const shareOnInstagram = async (imageUrl) => {
  try {
    const shareText = `Hey! Check out my health results ${imageUrl}`;
    // For Instagram story sharing, we can either:
    // 1. Copy link to clipboard and open Instagram
    await navigator.clipboard.writeText(shareText);
    // 2. Open Instagram and show a message to paste the link
    openInNewTab(`https://www.instagram.com`);
    alert('Link copied! You can paste it in your Instagram story or message.');
  } catch (error) {
    console.error('Failed to share on Instagram:', error);
    throw error;
  }
};

const ShareButtons = ({ cardRef, item, socialButtonsCss }) => {
  const isSmallScreen = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const [isSharing, setIsSharing] = useState(false);
  const [shareError, setShareError] = useState(null);
  const [sharedImageUrl, setSharedImageUrl] = useState('');
  const [open, setOpen] = useState(false);
  const [clientType, setClientType] = useState()
  const clientApi = useClientApi(clientType);

  // useEffect(() => {
  //   // Determine client type based on your authentication or route
  //   const determineClientType = () => {
  //     const fromDoktor = window.localStorage.getItem('from_doktor');
  //     if (fromDoktor === 'true') {
  //       return CLIENT_TYPES.DOKTOR_CONNECT;
  //     }
  //     // Add logic for ATP detection
  //     const fromATP = window.localStorage.getItem('from_atp');
  //     if (fromATP === 'true') {
  //       return CLIENT_TYPES.ATP;
  //     }
  //     return CLIENT_TYPES.DEFAULT;
  //   };

  //   setClientType(determineClientType());
  // }, []);

  useEffect(() => {
    setClientType(identifyClientType());
  }, []);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  // Get access token from localStorage
  const getAccessToken = async () => {
    const authTokens = localStorage.getItem('authTokens');
    return JSON.parse(authTokens).access_token;
  };

  const captureAndUploadImgForClient = async () => {
    console.log(cardRef);

    if (!cardRef.current) return null;
    try {
      const dataUrl = await domtoimage.toPng(cardRef.current);

      // Convert base64 to blob
      const response = await fetch(dataUrl);
      const blob = await response.blob();

      // Create FormData
      const formData = new FormData();
      formData.append('image_file', blob, 'health_status.png');

      //get session id 
      const sessionId = localStorage.getItem('session-id');

      try {
        const uploadResponse = await clientApi.postClientSocialShareImg(sessionId, formData);

        if (uploadResponse.data.filename) {
          try {
            const shareUrlResponse = await clientApi.getClientSocialShareImg(sessionId, uploadResponse.data.filename);
            return shareUrlResponse.data.presigned_url;
          } catch (error) {
            throw new Error("Failed to upload image.");
          }
        }
      } catch (error) {
        console.error("Error during image upload process:", error);
        throw new Error("Failed to upload image.");
      }

    } catch (error) {
      console.error('Error capturing and uploading image:', error);
      throw new Error("Failed to upload image.");
    }
  }

  // Capture and upload image
  const captureAndUploadImage = async () => {
    if (!cardRef.current) return null;
    try {
      const dataUrl = await domtoimage.toPng(cardRef.current);

      // Convert base64 to blob
      const response = await fetch(dataUrl);
      const blob = await response.blob();

      // Create FormData
      const formData = new FormData();
      formData.append('image_file', blob, 'health_status.png');

      // Get access token
      const accessToken = await getAccessToken();

      // Upload to backend
      const uploadResponse = await axios.post(
        `${BASE_URL}/results/result-image?assessment_id=${item.userAssessmentID}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${accessToken}`,
          }
        }
      );

      if (uploadResponse.data.filename) {
        const shareUrlResponse = await axios.get(
          `${BASE_URL}/results/get-result-screenshot/${uploadResponse.data.filename}?assessment_id=${item.userAssessmentID}`,
          {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
            }
          }
        );
        return shareUrlResponse.data.presigned_url;
      }
      throw new Error('Failed to get shareable URL');
    } catch (error) {
      console.error('Error capturing and uploading image:', error);
      throw error;
    }
  };

  // Handle share click
  const handleShareClick = async (platform, shareFunction, isclientFlow = false) => {
    try {
      setIsSharing(true);
      setShareError(null);

      let imageUrl;


      if (isclientFlow) {
        imageUrl = await captureAndUploadImgForClient();
      } else {
        imageUrl = await captureAndUploadImage();
      }

      setSharedImageUrl(imageUrl);


      if (imageUrl) {
        await shareFunction(imageUrl); // main share functionality according to app 
      }
    } catch (error) {
      console.error(`Error sharing to ${platform}:`, error);
      setShareError(`Unable to share to ${platform}. Please try again later.`);
      setTimeout(() => setShareError(null), 3000);
    } finally {
      setIsSharing(false);
    }
  };

  // Handle copy link
  const handleCopyLink = async () => {
    try {
      setIsSharing(true);
      setShareError(null);
      let imageUrl;

      if (socialButtonsCss?.isSocialIconForClients) {
        imageUrl = await captureAndUploadImgForClient();
      } else {
        imageUrl = await captureAndUploadImage();
      }
      await navigator.clipboard.writeText(imageUrl);


      alert('Link copied to clipboard!');
    } catch (error) {
      console.error('Error copying link:', error);
      setShareError('Unable to copy link. Please try again later.');
      setTimeout(() => setShareError(null), 3000);
    } finally {
      setIsSharing(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Stack
        display="flex"
        className="result-card-footer social-share-link"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        marginTop={1}
      >
        {/* WhatsApp Button */}

        {socialButtonsCss.isBgRequired ?
          <Button
            onClick={() => handleShareClick('WhatsApp', shareOnWhatsApp, socialButtonsCss.isSocialIconForClients)}
            disabled={isSharing}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '10px',
              backgroundColor: socialButtonsCss.background ? socialButtonsCss.background : 'transaparent',

              color: 'white',
              minWidth: '65px',
              minHeight: '65px',
              marginRight: '10px',
              borderRadius: '50%',
              display: 'flex',
              fontSize: '30px',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': {
                backgroundColor: socialButtonsCss.background ? socialButtonsCss.background : 'transaparent',
              },
            }}
           
          >
            { clientType == CLIENT_TYPES.DEFAULT  ? 
            <img src='/others/whatsAppIcon1.svg' style={{ height: "auto", width : isSmallScreen ? "46px" : "56px"}}/> :
            <FontAwesomeIcon icon={faWhatsapp} style={{ color: socialButtonsCss.color, height: isSmallScreen ? "44px" : "47px" }} />
             } 
          </Button>
          :
          <Button
            onClick={() => handleShareClick('WhatsApp', shareOnWhatsApp, true)}
            disabled={isSharing}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '10px',
            }}>
            <FontAwesomeIcon icon={faWhatsapp} style={{ color: socialButtonsCss.color, height: isSmallScreen ? "44px" : "47px" }} />
          </Button>
        }



        {/* Facebook Button */}
        {
          socialButtonsCss.isBgRequired ?
            <Button
              onClick={() => handleShareClick('Facebook', shareOnFacebook, socialButtonsCss.isSocialIconForClients)}
              disabled={isSharing}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '10px',
                backgroundColor: socialButtonsCss.background ? socialButtonsCss.background : 'transaparent',
                color: 'white',
                minWidth: '65px',
                minHeight: '65px',
                marginRight: '10px',
                borderRadius: '50%',
                display: 'flex',
                fontSize: '30px',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': {
                  backgroundColor: socialButtonsCss.background ? socialButtonsCss.background : 'transaparent',
                },

              }}

            >
               { clientType == CLIENT_TYPES.DEFAULT  ? 
                <img src='/others/facebookAImdIcon.svg' 
                style={{height: "auto",width : isSmallScreen ? "44px" : "54px"}}/> :
              <FontAwesomeIcon icon={faFacebookF} style={{ color: socialButtonsCss.color, height: isSmallScreen ? "34px" : "38px" }} />
               }
            </Button>
            :
            <Button
              onClick={() => handleShareClick('Facebook', shareOnFacebook, true)}
              disabled={isSharing}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '10px',
              }}>
              <FontAwesomeIcon icon={faFacebookF} style={{ color: socialButtonsCss.color, height: isSmallScreen ? "34px" : "38px" }} />
            </Button>
        }


        {/* Twitter Button */}
        {
          socialButtonsCss.isBgRequired ?
            <Button
              onClick={() => handleShareClick('Twitter', shareOnTwitter, socialButtonsCss.isSocialIconForClients)}
              disabled={isSharing}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '10px',
                backgroundColor: socialButtonsCss.background ? socialButtonsCss.background : 'transaparent',
                color: 'white',
                minWidth: '65px',
                minHeight: '65px',
                marginRight: '10px',
                borderRadius: '50%',
                display: 'flex',
                fontSize: '30px',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': {
                  backgroundColor: socialButtonsCss.background ? socialButtonsCss.background : 'transaparent',
                },

              }}

            >  { clientType == CLIENT_TYPES.DEFAULT  ? 
              <img src='/others/twitterAimdIcon.svg' 
              style={{height: "auto",width : isSmallScreen ? "44px" : "54px"}}/> :

              <FontAwesomeIcon icon={faXTwitter} style={{ color: socialButtonsCss.color, height: isSmallScreen ? "38px" : "43px" }} />
            }
            </Button>
            :
            <Button
              onClick={() => handleShareClick('Twitter', shareOnTwitter, true, socialButtonsCss.isSocialIconForClients)}
              disabled={isSharing}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '10px',
              }}>
              <FontAwesomeIcon icon={faXTwitter} style={{ color: socialButtonsCss.color, height: isSmallScreen ? "38px" : "43px" }} />
            </Button>
        }


        {/* Snapchat Button */}
        {
          socialButtonsCss.isBgRequired ?
            <Button
              onClick={() => handleShareClick('Snapchat', shareOnSnapchat, socialButtonsCss.isSocialIconForClients)}
              disabled={isSharing}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '10px',
                backgroundColor: socialButtonsCss.background ? socialButtonsCss.background : 'transaparent',
                color: 'white',
                minWidth: '65px',
                minHeight: '65px',
                marginRight: '10px',
                borderRadius: '50%',
                display: 'flex',
                fontSize: '30px',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': {
                  backgroundColor: socialButtonsCss.background ? socialButtonsCss.background : 'transaparent',
                },

              }}

            >
                { clientType == CLIENT_TYPES.DEFAULT  ? 
                <img src='/others/snapchat1.svg' 
                style={{height: "auto",width : isSmallScreen ? "46px" : "56px"}}/>
                :
              <FontAwesomeIcon icon={faSnapchatGhost} 
              style={{ color: socialButtonsCss.color, height: isSmallScreen ? "41px" : "46px" }}/>
                 }
            </Button>
            :
            <Button
              onClick={() => handleShareClick('Snapchat', shareOnSnapchat, true)}
              disabled={isSharing}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '10px',
              }}>
              <FontAwesomeIcon icon={faSnapchatGhost} style={{ color: socialButtonsCss.color, height: isSmallScreen ? "41px" : "46px" }} />
            </Button>
        }


        {/* More Options Button */}
        {
          socialButtonsCss.isBgRequired ?
            <Button
              onClick={handleClickOpen}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '10px',
                backgroundColor: socialButtonsCss.background ? socialButtonsCss.background : 'transaparent',
                color: 'white',
                minWidth: '65px',
                minHeight: '65px',
                marginRight: '10px',
                borderRadius: '50%',
                display: 'flex',
                fontSize: '30px',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': {
                  backgroundColor: socialButtonsCss.background ? socialButtonsCss.background : 'transaparent',
                },
              }}

            >
             { clientType == CLIENT_TYPES.DEFAULT  ? 
                <img src='/others/shareAimdIcon.svg' 
                style={{height: "auto",width : isSmallScreen ? "46px" : "56px"}}
                />
                :
              <FontAwesomeIcon icon={faShare} style={{ color: socialButtonsCss.color, height: isSmallScreen ? "38px" : "46px" }} />
             }
            </Button>
            :
            <Button
              onClick={handleClickOpen}
              disabled={isSharing}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '10px',
              }}>
              <FontAwesomeIcon icon={faShare} style={{ color: socialButtonsCss.color, height: isSmallScreen ? "38px" : "46px" }} />
            </Button>
        }

      </Stack>

      {/* Error message */}
      {shareError && (
        <Typography color="error" align="center" sx={{ mt: 2 }}>
          {shareError}
        </Typography>
      )}

      {/* More options dialog */}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle fontSize="18px">Share With Others</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Box display="flex" justifyContent="center">
              {/* Instagram Button */}
              <Button
                onClick={async () => {
                  try {
                    const shareText = `Hey! Check out my health results ${sharedImageUrl}`;
                    await navigator.clipboard.writeText(shareText);
                    openInNewTab('https://www.instagram.com');
                    alert('Link copied! You can paste it in your Instagram story or message.');
                    handleClose(); // Close the dialog after copying
                  } catch (error) {
                    console.error('Error with Instagram share:', error);
                    setShareError('Unable to copy link for Instagram share.');
                  }
                }}
                sx={{
                  backgroundColor : socialButtonsCss.background ? socialButtonsCss.background : '#eb506f',
                  color: socialButtonsCss.color ? socialButtonsCss.color :  'white',
                  minWidth: '36px',
                  minHeight: '36px',
                  marginRight: '6px',
                  borderRadius: '50%',
                  fontSize: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': {
                    backgroundColor : socialButtonsCss.background ? socialButtonsCss.background : '#eb506f',
                  },
                }}
              >
                <FontAwesomeIcon icon={faInstagram}  />
              </Button>
               
              {/* Copy Link Button */}
              <Button
                onClick={handleCopyLink}
                disabled={isSharing}
                sx={{
                  backgroundColor : socialButtonsCss.background ? socialButtonsCss.background : 'gray',
                  color: socialButtonsCss.color ? socialButtonsCss.color :  'white',
                  minWidth: '36px',
                  minHeight: '36px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': {
                    backgroundColor : socialButtonsCss.background ? socialButtonsCss.background : 'primary.dark',
                  },
                }}
              >
                <FontAwesomeIcon icon={faLink} />
              </Button>
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShareButtons;